import { callAllHandlers } from "@smartsheet/lodestar-utils";
import { AllHTMLAttributes, useCallback, useEffect, useRef, useState } from "react";

export type UseDelayedOpenOptions = {
    /**
     * If set to "each", the Flyout will show after `enterDelayMs` ms on mouseEnter/focus and will
     * disappear after `exitDelayMs` on mouseExit/blur.
     *
     * If set to "batched", subsequent mouseEnter/focus within `exitDelayMs` ms will cause other flyouts
     * using this hook to show immediately, rather than waiting `enterDelayMs` ms again. This can be
     * used  to create a "flyout" or "help" mode where things show quickly if you're looking at
     * many  Flyout-based components.
     */
    delayMode?: "each" | "batch";

    /**
     * If specified, will adjust how quickly in ms the flyout opens after mouse enters on button.
     * Default is 500ms
     */
    enterDelay?: number;

    /**
     * If specified, will adjust how quickly in ms the flyout closes after mouse leaves.
     * Default is 500ms
     */
    exitDelay?: number;

    /**
     * Callback to be called when the flyout is closed.
     */
    onCloseCallback?: () => void;

    /**
     * Callback to be called when the flyout is opened.
     */
    onOpenCallback?: () => void;

    /**
     * If delayMode === "batch", you can set this to change the number of milliseconds this hook
     * will wait before opening another flyout after `enterDelayMs` milliseconds.
     */
    resetDelay?: number;
};

export interface UseDelayedOpenResult<TargetElement> {
    /**
     * Can be used to cancel an outstanding enter timer if you want to prevent the flyout from
     * opening in a mouseEnter/focus event.
     *
     * You'll probably want to call this within a requestAnimationFrame since we start the timer
     * within a setTimeout.
     */
    cancelEnterTimer: () => void;

    /**
     * Can be used to cancel an outstanding close timer if you want to prevent the flyout from closing
     * in a mouseEnter event.
     *
     */
    cancelExitDelayTimer: () => void;

    /**
     * Returns event handlers that should be spread onto the Flyout's target.
     * If you'd like to include your own, please pass it in here and it will be merged
     * into the respective handler.
     */
    getDelayedOpenTargetProps: (
        overrides?: AllHTMLAttributes<TargetElement> & {
            onBlur?: React.FocusEventHandler<TargetElement>;
            onFocus?: React.FocusEventHandler<TargetElement>;
            onMouseEnter?: React.MouseEventHandler<TargetElement>;
            onMouseLeave?: React.MouseEventHandler<TargetElement>;
        }
    ) => {
        onBlur: React.FocusEventHandler<TargetElement>;
        onFocus: React.FocusEventHandler<TargetElement>;
        onMouseEnter: React.MouseEventHandler<TargetElement>;
        onMouseLeave: React.MouseEventHandler<TargetElement>;
    };

    isOpen: boolean;

    /**
     * Will reset the internal isOpen state. Respects the given enter and exit delays.
     */
    setIsOpen: (newIsOpen: boolean) => void;
}

const NO_CLOSE_DELAY = {
    timer: -1,
    closeHandler: () => {},
};

// Helps control delay when hovering back-to-back over items w/ flyouts.
// Hovering back-to-back when flyout is showing will ignore initial flyout delay.
// These are here instead of useState because they are global for all flyout target refs.
let ignoreEnterDelay = false;
let ignoreEnterDelayTimer: number;
let closeDelay = NO_CLOSE_DELAY;

// We don't want to allow tests to interact with each other. Sometimes if a Flyout doesn't
// go away quickly enough (tooltips, for instance) the next test will render one before
// these flags are reset. We always want to reset these between tests. See jest.setup.ts.
let resetUseDelayedOptionForTest = (): void => undefined;
if (process.env.NODE_ENV !== "production") {
    // Don't ship this code in production.
    resetUseDelayedOptionForTest = () => {
        ignoreEnterDelay = false;
        clearTimeout(ignoreEnterDelayTimer);
    };
}
export { resetUseDelayedOptionForTest };

const endCloseDelay = () => {
    window.clearTimeout(closeDelay.timer);
    closeDelay.closeHandler();
    closeDelay = NO_CLOSE_DELAY;
};

/**
 * This hook provides an isOpen flag and HTML handlers to attach to an element to mutate it. It can
 * be used for elements that should show a popup after a short time like a contact card, tooltip,
 * etc. For things like tooltips, it supports a delayMode argument that allows the floating elements
 * to interact with each other so that opening subsequent elements is faster after the first one
 * opens. This provides a nice UX when a user is trying to look at multiple tooltips.
 */
export const useDelayedOpen = <TargetElementType extends HTMLElement = HTMLButtonElement>({
    exitDelay,
    enterDelay = 500,
    resetDelay = 500,
    delayMode = "batch",
    onOpenCallback = () => {},
    onCloseCallback = () => {},
}: UseDelayedOpenOptions = {}): UseDelayedOpenResult<TargetElementType> => {
    const [isOpen, setIsOpen] = useState(false);

    const enterTimer = useRef<number | undefined>();

    const handleOpen = useCallback(() => {
        window.clearTimeout(ignoreEnterDelayTimer);

        if (delayMode === "batch") {
            ignoreEnterDelay = true;
        }
        setIsOpen(true);
        onOpenCallback();
    }, [delayMode, onOpenCallback]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        onCloseCallback();
    }, [onCloseCallback]);

    const startCloseDelay = useCallback(
        (closeHandler: () => void) => {
            endCloseDelay();
            closeDelay = {
                timer: window.setTimeout(() => {
                    endCloseDelay();
                }, exitDelay),
                closeHandler,
            };
        },
        [exitDelay]
    );

    const maybeDelayedOpen = useCallback(() => {
        // This ensures that the flyout doesn't open after you have quickly moved away from element.
        window.clearTimeout(enterTimer.current);

        endCloseDelay();
        if (delayMode === "each" || !ignoreEnterDelay) {
            enterTimer.current = window.setTimeout(() => {
                handleOpen();
            }, enterDelay);
        } else {
            handleOpen();
        }
    }, [delayMode, enterDelay, handleOpen]);

    const maybeDelayedClose = useCallback(() => {
        /*
           Sadly cannot do the following since not all calls here have an associated event or a relatedTarget (if the relatedTarget is not focusable).
           This means there is a case that if a Tooltip Flyout is in the way of an element about to gain onMouseEnter or onFocus,
            the overlapped element's Tooltip will not be displayed since the original elements onBlur (when passing over the blocking tooltip)
            will cancel the "delayed" open for any other tooltip.

            const relatedTarget = evt?.relatedTarget as HTMLElement;
            if ( relatedTarget && relatedTarget.closest && relatedTarget.closest('[role="tooltip"]')) {
                console.log("I'm a tooltip");
                return;
            }
         */

        // this ensures that the flyout doesn't open after you have quickly moved away from element.
        window.clearTimeout(enterTimer.current);

        window.clearTimeout(ignoreEnterDelayTimer);
        if (delayMode === "batch") {
            ignoreEnterDelayTimer = window.setTimeout(() => {
                ignoreEnterDelay = false;
            }, resetDelay);
        }

        if (exitDelay) {
            startCloseDelay(handleClose);
        } else {
            handleClose();
        }
    }, [handleClose, delayMode, resetDelay, exitDelay, startCloseDelay]);

    // Clean up timers when unmounted.
    useEffect(() => {
        return () => {
            endCloseDelay();
            window.clearTimeout(enterTimer.current);
            window.clearTimeout(ignoreEnterDelayTimer);
        };
    }, []);

    return {
        setIsOpen: useCallback(
            (newIsOpen: boolean) => (newIsOpen ? maybeDelayedOpen() : maybeDelayedClose()),
            [maybeDelayedOpen, maybeDelayedClose]
        ),
        cancelEnterTimer: useCallback(() => {
            ignoreEnterDelay = false;
            window.clearTimeout(enterTimer.current);
        }, []),
        isOpen,
        getDelayedOpenTargetProps: ({ onMouseEnter, onMouseLeave, onFocus, onBlur, ...restOverrides } = {}) => ({
            ...restOverrides,
            onMouseEnter: callAllHandlers(onMouseEnter, maybeDelayedOpen),
            onMouseLeave: callAllHandlers(onMouseLeave, maybeDelayedClose),
            onFocus: callAllHandlers(onFocus, maybeDelayedOpen),
            onBlur: callAllHandlers(onBlur, maybeDelayedClose),
        }),
        cancelExitDelayTimer: endCloseDelay,
    };
};
